import {
  GiftSubscribeCard,
  GiftSubscribeCardProps,
  OverlapPreviewProps,
  giftSubscribeCardKeyBuilder,
} from "@bluebottlecoffee/design-system/components";
import { getLocale } from "../integrations/date-fns/get-locale";
import { Dialect } from "../utils/locale";

export type GiftSubscribeCardSansModal = Omit<
  GiftSubscribeCardProps,
  "modalContent" | "modalTriggerText"
>;

type GiftSubsOverlapPreviewTransformerProps = {
  productCards: GiftSubscribeCardSansModal[];
  viewMoreCtaText: string;
  dialect: Dialect;
};

/** Tab data before the view-more CTA count is applied (gift subs revamp variant). */
export type GiftSubsTabbedPreviewTabDraft<TCard> = {
  id: string;
  label: string;
  cards: TCard[];
};

type GiftSubsTabbedPreviewTransformerProps<TCard> = {
  tabs: GiftSubsTabbedPreviewTabDraft<TCard>[];
  viewMoreCtaText: string;
  dialect: Dialect;
};

function formatViewMoreCtaText(
  viewMoreCtaText: string,
  productCount: number,
  dialect: Dialect,
): string {
  const locale = getLocale(dialect);

  return viewMoreCtaText.replace(
    "##",
    productCount.toLocaleString(locale.code),
  );
}

export function toGiftSubsOverlapPreviewProps({
  productCards,
  viewMoreCtaText,
  dialect,
}: GiftSubsOverlapPreviewTransformerProps): OverlapPreviewProps<GiftSubscribeCardSansModal> {
  const ctaText = formatViewMoreCtaText(
    viewMoreCtaText,
    productCards.length,
    dialect,
  );

  return {
    Card: GiftSubscribeCard,
    cards: productCards,
    ctaText,
    keyBuilder: giftSubscribeCardKeyBuilder,
  };
}

/** Builds tab props for {@link TabbedProductPreview} with a per-tab product count in the CTA. */
export function toGiftSubsTabbedPreviewTabs<TCard>({
  tabs,
  viewMoreCtaText,
  dialect,
}: GiftSubsTabbedPreviewTransformerProps<TCard>): Array<
  GiftSubsTabbedPreviewTabDraft<TCard> & { ctaText: string }
> {
  return tabs.map((tab) => ({
    ...tab,
    ctaText: formatViewMoreCtaText(
      viewMoreCtaText,
      tab.cards.length,
      dialect,
    ),
  }));
}
